import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { SpinnerComponent } from '../shared/spinner/spinner.component';
import { NewsFeed, PanelState } from '../../beans/dashboard/dashboard-module.bean';
import { JwtHelper } from '../../services/core/jwt-helper.service';
import { FeedsService } from '../../services/dashboard/feeds.service';
import { MatDialog } from '@angular/material/dialog';
import { Environment } from '../../config/environment';
import { AuthService } from '../../services/core/auth.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { HttpClient } from '@angular/common/http';
import { TutorialComponent } from '../tutorial/tutorial.component';
import { TutorialService } from 'src/app/services/tutorial/tutorial.service';
import { ProfileRelatedInfoBean } from 'src/app/beans/account/account-module.bean';
import { USER_LEVEL } from '../../constants/user.level.constants';
import { tutorialDisabled } from 'src/app/constants/app.constants';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataCarrierService } from 'src/app/services/core/data-carrier.service';
import { NonMatchedUserModalComponent } from './nonmatched-user-modal/nonmatched-user-modal.component';
import {
	AnalyticsAuthPageViewBean,
	AuthEventValueBean,
	AnalyticsUserBean,
} from 'src/app/beans/analytics/analytics-module.bean';
import { CookieService } from 'ngx-cookie-service';
import { ModalService } from '../../services/core/modal.service';
import { MemberInfoConfirmationModalComponent } from '../shared/member-info-confirmation-modal/member-info-confirmation-modal.component';
import { MemberInfoConfirmationService } from 'src/app/services/dashboard/member-info-confirmation.service';
import { APP_USER_PERMISSIONS } from 'src/app/constants/app-user-permissions.constants';
import { ProfileService } from 'src/app/services/core/profile.service';
import { Config } from 'src/app/config/config';

/**
 * Time option interface required by newsfeeds
 */
export interface TimeOption {
	/** value to be viewed in UI */
	viewValue: string;
	/** actual value */
	value: number;
	/** callable function */
	callable: () => void;
}

/**
 * Dashboard component
 */
@Component({
	selector: 'app-dashboard',
	templateUrl: './dashboard.component.html',
	styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy, AfterViewInit {
	/**
	 * Flag to decide if member is matched or not
	 */
	isMatchedMember: boolean = false;
	/**
	 * Holds list of news feeds
	 */
	feeds: NewsFeed[] = [];
	/**
	 * Flag to check if component data is loaded
	 */
	isLoaded: boolean = false;
	/**
	 * Holds profile related info of member
	 */
	profileRelatedInfo: ProfileRelatedInfoBean;
	/**
	 * Holds environment object
	 */
	env: any;
	/**
	 * Behaviour subject object
	 */
	private _destroyed$ = new Subject();
	/**
	 * Holds user level enum objects
	 */
	USER_LEVEL = USER_LEVEL;
	MODULE_LEVEL = APP_USER_PERMISSIONS;
	/** analytics auth page bean */
	analyticsAuthPageViewBean: AnalyticsAuthPageViewBean;
	/** Holds panel status of the user */
	panelStatus: PanelState;

	timeInterval: Subscription;
	// TODO: Declared but not used
	timeOptions;

	/**
	 * Dashboard component constructor
	 * @param appointmentService - appointment service
	 * @param authService - auth service
	 * @param dialog - dialog service
	 * @param environment - to provide environment specific values
	 * @param feedsService - feeds service to fetch feeds
	 * @param http - http client required by analytics
	 * @param spinner - spinner component
	 * @param tutorialService - tutorial service
	 * @param dataCarrierService - data carrier - behaviour subject
	 * @param jwtService - jwt service to get epms pid
	 */
	constructor(
		private authService: AuthService,
		private dialog: MatDialog,
		private environment: Environment,
		private feedsService: FeedsService,
		http: HttpClient,
		private spinner: SpinnerComponent,
		private tutorialService: TutorialService,
		private dataCarrierService: DataCarrierService,
		private jwtService: JwtHelper,
		cookieService: CookieService,
		private modalService: ModalService,
		private memberInfoService: MemberInfoConfirmationService,
		private config: Config,
		private profileService: ProfileService,
		private analyticsService: AnalyticsService,
	) {
		this.spinner.stopLoader();
		this.env = this.environment.getEnvironment();
		this.analyticsAuthPageViewBean = new AnalyticsAuthPageViewBean();
	}

	/**
	 * On component initialization
	 */
	ngOnInit() {
		document.body.classList.add('dashboard-bg');
		this.spinner.startLoader();
		this.getNewsFeeds();
	}

	/**
	 * After component initialization
	 */
	ngAfterViewInit() {
		// Checks for forgot password toaster value in session to show toaster
		if (sessionStorage.getItem('forgotPassToaster') && sessionStorage.getItem('forgotPassToaster') === '1') {
			this.modalService.showSuccessToaster('Your password has been changed successfully!');
			sessionStorage.removeItem('forgotPassToaster');
		}
		// Checks for create account toaster value in session to show toaster
		if (sessionStorage.getItem('createAccountToaster') && sessionStorage.getItem('createAccountToaster') === '1') {
			this.modalService.showSuccessToaster('Welcome! Thank you for being proactive about your health.');
			sessionStorage.removeItem('createAccountToaster');
		}
	}

	/**
	 * Popup to be opened for non-matched users - to take action on their account by contacting team ehe
	 */
	openNonmatchedUserPopup() {
		const dialogRef = this.dialog.open(NonMatchedUserModalComponent, {
			width: '600px',
			disableClose: true,
		});
	}

	/**
	 * Method to check if tutorial popup should be shown for user on login
	 */
	tutorialCheck() {
		/**
		 * Global flag for tutorials
		 */
		if (!tutorialDisabled) {
			this.tutorialService
				.showTutorial()
				.pipe(takeUntil(this._destroyed$))
				.subscribe(data => {
					const { showTutorial } = data;
					if (showTutorial === true) {
						const dialogRef = this.dialog.open(TutorialComponent, {
							width: '750px',
							disableClose: true,
						});

						dialogRef
							.afterClosed()
							.pipe(takeUntil(this._destroyed$))
							.subscribe(result => {
								const postBody = { status: result };
								this.tutorialService
									.postStatus(postBody)
									.pipe(takeUntil(this._destroyed$))
									.subscribe(postResult => {});
							});
					}
				});
		}
	}

	/**
	 * Sets profile info in localstorage and profileRelatedInfo attribute
	 * @param profileRelatedInfo profileInfo - event from header component
	 */
	getProfileInfo(profileRelatedInfo: ProfileRelatedInfoBean) {
		if (profileRelatedInfo.noMatch === true) {
			this.profileRelatedInfo = profileRelatedInfo;
			localStorage.setItem('isMatchedUser', 'false');
		} else {
			this.profileRelatedInfo = profileRelatedInfo;
			this.isMatchedMember = this.profileRelatedInfo.profileInfo.userMatched;
			if (this.isMatchedMember) {
				this.tutorialCheck();
				this.memberInfoModalCheck();
			}
			this.fetchData();
		}
	}

	/**
	 * Sets analytics related data and push the same to auth bucket
	 */
	fetchData() {
		let email, epmsPID;
		const ssoType = localStorage.getItem('sso-type');
		if (!ssoType) {
			const auth = sessionStorage.getItem('auth');
			if (auth) {
				email = JSON.parse(sessionStorage.getItem('auth')).idToken.payload.email;
				epmsPID = JSON.parse(sessionStorage.getItem('auth')).idToken.payload['custom:epms-p-id'];
			}
		} else {
			epmsPID = this.jwtService.getEpmsId();
			this.getLoginStatus();
		}
		const user = new AnalyticsUserBean();
		user.email = email;
		user.uuid = epmsPID;
		user.company = this.profileRelatedInfo.profileInfo.client.name;
		user.eligibleForPE = this.profileRelatedInfo.profileInfo.eligibleForPE;
		user.userMatched = this.profileRelatedInfo.profileInfo.userMatched;
		user.personaType = this.profileRelatedInfo.personaType;
		if (ssoType) {
			user.ssoType = ssoType;
		}
		this.analyticsAuthPageViewBean.eventLabel = 'pageView';
		this.analyticsAuthPageViewBean.eventComponent = '/dashboard';
		const authEventValueBean = new AuthEventValueBean();
		authEventValueBean.user = user;
		authEventValueBean.utm = JSON.parse(localStorage.getItem('utm'));
		authEventValueBean.status = '';
		this.analyticsAuthPageViewBean.eventValue = authEventValueBean;
		this.analyticsService.pushData(this.analyticsAuthPageViewBean);
	}

	getLoginStatus() {
		if (window[this.config.userLoginSession.sessionCheckIntervalVarName]) {
			clearInterval(window[this.config.userLoginSession.sessionCheckIntervalVarName]);
		}
		window[this.config.userLoginSession.sessionCheckIntervalVarName] = setInterval(() => {
			this.profileService.GetSessionInfo().subscribe(res => {
				if (res.message === 'Session Closed') {
					this.modalService.openSessionExpireModal(
						'SESSION EXPIRED',
						'You have an active session in another device. Please login to continue'
					);
					this.profileService.inactivateSession().subscribe(response => this.authService.logoutUser());
				}
			});
		}, this.config.userLoginSession.sessionCheckIntervalSeconds * 1000);
	}

	/**
	 * Gets news feeds of the user and sends the same to other related components
	 */
	getNewsFeeds() {
		const self = this;
		self.feedsService
			.getNewsFeeds()
			.pipe(takeUntil(this._destroyed$))
			.subscribe(
				(data: any) => {
					self.isLoaded = true;
					self.feeds = data.feeds;
					// Since images are converted to base64 encoded form - assigning image to temporary value
					if (data.feeds) {
						data.feeds.forEach(feed => {
							if (feed.description) {
								feed.description = feed.description.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
							}
						});
					}
					if (data.panelStatus) {
						self.isMatchedMember = data.panelStatus.matched;
						self.panelStatus = data.panelStatus;
						localStorage.setItem('status', JSON.stringify(self.panelStatus));
						this.dataCarrierService.pushValue('panelStatus', self.panelStatus);
						if (!data.panelStatus.matched) {
							this.openNonmatchedUserPopup();
						}
					} else {
						self.isMatchedMember = false;
					}
					this.spinner.stopLoader();
				},
				err => {
					this.spinner.stopLoader();
				}
			);
	}

	/** checks if member account info modal should be shown
	 * upon arriving to dashboard
	 */
	memberInfoModalCheck() {
		this.memberInfoService.showMemberInfoModal().subscribe(response => {
			const { showMemberAccountInfo } = response;
			if (showMemberAccountInfo === true) {
				const dialogRef = this.dialog.open(MemberInfoConfirmationModalComponent, {
					width: '750px',
					height: '570px',
					disableClose: true,
					panelClass: 'overlay-no-padding',
				});
				dialogRef.afterClosed().subscribe(result => {
					const postBody = { value: result };
					this.memberInfoService.postConfirmation(postBody).subscribe(postRes => {});
				});
			}
		});
	}

	sortFeeds(event) {
		// TODO: Declared but not used
	}

	/**
	 * Destroys dashboard component
	 */
	ngOnDestroy(): void {
		document.body.classList.remove('dashboard-bg');
		this._destroyed$.next(undefined);
		this._destroyed$.complete();
	}
}
